/** Typed calls to the Casa API. */
import { config } from "./config";

export interface XeroStatus {
  connected: boolean;
  tenant_name?: string;
  has_payroll?: boolean;
  /** Xero's website address code, once looked up; empty until then. */
  short_code?: string;
}

export interface Client {
  client_id: string;
  name: string;
  abn: string;
  entity_type: string;
  gst_basis: "Cash" | "Accrual";
  frequency: "Monthly" | "Quarterly" | "Annual";
  xero: XeroStatus;
}

export interface NewClient {
  name: string;
  abn: string;
  entity_type: string;
  gst_basis: string;
  frequency: string;
}

export class ApiError extends Error {
  // Written out rather than using parameter properties: `erasableSyntaxOnly`
  // rejects syntax that cannot be removed by type stripping alone.
  readonly status: number;
  readonly field: string | undefined;

  constructor(status: number, message: string, field?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.field = field;
  }
}

async function apiRequest<T>(
  path: string,
  token: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      Authorization: `Bearer ${token}`,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
    },
  });

  if (response.status === 204) return undefined as T;

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  if (!response.ok) {
    const body = payload as { message?: string; field?: string };
    throw new ApiError(
      response.status,
      body.message ?? `Request failed (${response.status})`,
      body.field,
    );
  }
  return payload as T;
}

export async function listClients(token: string): Promise<Client[]> {
  const data = await apiRequest<{ clients: Client[] }>("/clients", token);
  return data.clients;
}

export async function createClient(token: string, client: NewClient): Promise<Client> {
  return apiRequest<Client>("/clients", token, {
    method: "POST",
    body: JSON.stringify(client),
  });
}

export async function updateClient(
  token: string,
  clientId: string,
  client: NewClient,
): Promise<Client> {
  return apiRequest<Client>(`/clients/${encodeURIComponent(clientId)}`, token, {
    method: "PUT",
    body: JSON.stringify(client),
  });
}

/** What deleting a client actually did, so the UI can report it rather than guess. */
export interface ClientRemoval {
  deleted: boolean;
  client_id: string;
  name: string;
  /** revoked | already_disconnected | unreachable | not_connected */
  xero: string;
  items: number;
  workpapers: number;
}

/**
 * Delete a client, its workpapers, and Casa's access to its Xero organisation.
 *
 * The Xero revocation happens first and a failure there aborts the whole
 * thing, so a rejected call means nothing was removed.
 */
export async function deleteClient(
  token: string,
  clientId: string,
): Promise<ClientRemoval> {
  return apiRequest<ClientRemoval>(`/clients/${encodeURIComponent(clientId)}`, token, {
    method: "DELETE",
  });
}

/**
 * Begin connecting a Xero organisation.
 *
 * Returns the URL to send the browser to. The caller navigates rather than
 * opening a popup, because Xero's consent screen will not render in one.
 */
export async function startXeroConnect(
  token: string,
  clientId: string,
  includePayroll: boolean,
): Promise<string> {
  const data = await apiRequest<{ authorize_url: string }>("/admin/xero/connect", token, {
    method: "POST",
    body: JSON.stringify({ client_id: clientId, include_payroll: includePayroll }),
  });
  return data.authorize_url;
}

// --------------------------------------------------------------------------
// Workpaper generation
// --------------------------------------------------------------------------
export interface Job {
  job_id: string;
  client_id: string;
  status: "queued" | "fetching" | "generating" | "succeeded" | "failed";
  progress: string;
  /** How the period reads on the workpaper, e.g. "Apr-Jun 2026". */
  period: string;
  /** ISO dates. Present so runs can be grouped and ordered by the quarter
   *  they are for -- the label cannot be sorted. */
  period_start: string;
  period_end: string;
  basis: string;
  is_terminal: boolean;
  /** Epoch seconds. Formatted in the browser, so it reads in local time. */
  created_at: number;
  /** Epoch seconds, 0 while the job is still running. */
  completed_at: number;
  workpaper_key: string;
  error: string;
  stats: Record<string, unknown>;
  /** The GST coding review, which runs separately and never fails the job. */
  review: Review;
}

export interface Review {
  status: "not_run" | "running" | "reviewed" | "failed";
  progress: string;
  /** How many lines carried a finding. */
  findings: number;
  /** How many lines were read. */
  lines: number;
  /** Epoch seconds, 0 until it has run. */
  reviewed_at: number;
  error: string;
}

export interface GenerateRequest {
  client_id: string;
  period_start: string;
  period_end: string;
  period_label?: string;
  basis?: string;
}

/**
 * Start a generation run.
 *
 * Returns as soon as the job is queued, not when the workpaper is ready --
 * reading a cash-basis client's whole ledger takes minutes, far longer than
 * API Gateway will hold a request open.
 */
export async function startWorkpaper(token: string, request: GenerateRequest): Promise<Job> {
  return apiRequest<Job>("/workpapers", token, {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/** A client's past workpapers, newest first. */
export async function listWorkpapers(token: string, clientId: string): Promise<Job[]> {
  const data = await apiRequest<{ workpapers: Job[] }>(
    `/clients/${encodeURIComponent(clientId)}/workpapers`,
    token,
  );
  return data.workpapers;
}

/** Remove a generated workpaper. Admin-only, server-side. */
export async function deleteWorkpaper(token: string, jobId: string): Promise<void> {
  await apiRequest<unknown>(`/workpapers/${encodeURIComponent(jobId)}`, token, {
    method: "DELETE",
  });
}

export async function getJob(token: string, jobId: string): Promise<Job> {
  return apiRequest<Job>(`/workpapers/${encodeURIComponent(jobId)}`, token);
}

/**
 * Run the GST coding review over a finished workpaper.
 *
 * Separate from generation on purpose: a workpaper is regenerated constantly
 * during review, and re-running the model each time would spend money nobody
 * asked for. Pressing the button is the consent.
 *
 * `force` re-runs one that has already been reviewed. That rebuilds the
 * workbook, so any verdict already typed into it is lost -- the caller must
 * say so deliberately.
 */
export async function startReview(
  token: string,
  jobId: string,
  force = false,
): Promise<Job> {
  const query = force ? "?force=true" : "";
  return apiRequest<Job>(
    `/workpapers/${encodeURIComponent(jobId)}/review${query}`,
    token,
    { method: "POST" },
  );
}

/**
 * Fetch a finished workpaper.
 *
 * The bytes come back through the authenticated API, not via a presigned S3
 * link: a presigned URL works for anyone holding it and ends up in browser
 * history, which is a poor trade for client financial data.
 */
export async function downloadWorkpaper(
  token: string,
  jobId: string,
  fallbackName?: string,
): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(
    `${config.apiBaseUrl}/workpapers/${encodeURIComponent(jobId)}/download`,
    { headers: { Authorization: `Bearer ${token}` } },
  );

  if (!response.ok) {
    let message = `Download failed (${response.status})`;
    try {
      const body = (await response.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      // A non-JSON error body tells us nothing useful; keep the status.
    }
    throw new ApiError(response.status, message);
  }

  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get("Content-Disposition"), fallbackName),
  };
}

/**
 * Pull the server's chosen filename out of Content-Disposition.
 *
 * `filename*` first: it is the RFC 5987 form and the only one that carries a
 * name not spelled in ASCII. `filename` is the plain fallback beside it.
 *
 * The header is only readable because the API lists it in
 * Access-Control-Expose-Headers. Without that this returns null across
 * origins, which is how every download quietly arrived under one generic
 * name -- so the caller's fallback names the file after the client rather
 * than leaving the browser to number the collisions.
 */
function filenameFrom(header: string | null, fallback?: string): string {
  const encoded = header?.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // Malformed percent-encoding; the plain form below is still worth a try.
    }
  }
  const plain = header?.match(/filename="([^"]+)"/);
  return plain?.[1] ?? fallback ?? "BAS Workpaper.xlsx";
}

/** Hand a blob to the browser as a download, then release it. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoking immediately can cancel the download in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// --------------------------------------------------------------------------
// Staff accounts
//
// Cognito is the directory; these proxy the few operations an admin performs,
// so adding a preparer does not need an AWS console login.
// --------------------------------------------------------------------------
export interface StaffUser {
  username: string;
  email: string;
  /** Cognito's own vocabulary. FORCE_CHANGE_PASSWORD means invited, never signed in. */
  status: string;
  enabled: boolean;
  roles: string[];
  created_at: number;
  /** How they sign in. Microsoft staff have no password, so Cognito reports
   *  them as FORCE_CHANGE_PASSWORD for good; this is what to show instead. */
  sign_in: "microsoft" | "microsoft_pending" | "password";
}

export interface StaffList {
  users: StaffUser[];
  /** The groups that exist, newest authority first. Read from Cognito, not hardcoded. */
  roles: string[];
}

export async function listStaff(token: string): Promise<StaffList> {
  return apiRequest<StaffList>("/admin/users", token);
}

/** Invite a staff member. Cognito mails the temporary password; we never see it. */
export async function inviteStaff(
  token: string,
  email: string,
  roles: string[],
): Promise<StaffUser> {
  return apiRequest<StaffUser>("/admin/users", token, {
    method: "POST",
    body: JSON.stringify({ email, roles }),
  });
}

/** Change roles, or enable/disable. Omitted fields are left alone. */
export async function updateStaff(
  token: string,
  username: string,
  change: { roles?: string[]; enabled?: boolean },
): Promise<StaffUser> {
  return apiRequest<StaffUser>(`/admin/users/${encodeURIComponent(username)}`, token, {
    method: "PUT",
    body: JSON.stringify(change),
  });
}

export async function deleteStaff(token: string, username: string): Promise<void> {
  await apiRequest<unknown>(`/admin/users/${encodeURIComponent(username)}`, token, {
    method: "DELETE",
  });
}

export async function resendInvite(token: string, username: string): Promise<void> {
  await apiRequest<unknown>(
    `/admin/users/${encodeURIComponent(username)}/invite`,
    token,
    { method: "POST" },
  );
}

// ---------------------------------------------------------------------------
// What changed between two runs
// ---------------------------------------------------------------------------

/** One line that moved between two runs of the same period. */
export interface LineChange {
  kind: "recoded" | "amount" | "redated" | "added" | "removed";
  date: string;
  account: string;
  details: string;
  reference: string;
  gross: string;
  /** Signed, as a string: a float here would make 1B turn on binary rounding. */
  gst_delta: string;
  source_type: string;
  source_id: string;
  from_code: string;
  to_code: string;
  /**
   * A link that opens this transaction in Xero, or "" when its document type
   * has no established deep link. Built by the API, which holds the only
   * copy of Xero's path table and the organisation shortcode.
   */
  xero_url: string;
}

export interface DiffSummary {
  unchanged: number;
  recoded: number;
  amount: number;
  redated: number;
  added: number;
  removed: number;
  /** What 1B shifts by between the two runs. A string, for the same reason. */
  gst_moved: string;
}

/** An earlier run of the same period that this one could be compared against. */
export interface DiffOption {
  job_id: string;
  created_at: number;
  reviewed: boolean;
  findings: number;
  /**
   * Whether that run stored the ledger it saw. Runs from before baselines
   * existed did not; they are offered as unavailable rather than hidden, so
   * looking for the run you reviewed tells you why it is not there.
   */
  diffable: boolean;
}

export interface WorkpaperDiff {
  job: Job;
  options: DiffOption[];
  comparable: boolean;
  /** Present when `comparable` is false. */
  reason?: "no_earlier_run" | "no_baseline" | "unreadable_baseline";
  against: {
    job_id: string;
    created_at?: number;
    generated?: string;
    reviewed?: boolean;
    findings?: number;
    lines?: number;
  } | null;
  lines?: number;
  summary?: DiffSummary;
  changes?: LineChange[];
  /** True when there were more changes than the response carries. */
  truncated?: boolean;
}

/**
 * What changed in the ledger since an earlier run of the same period.
 *
 * Deterministic: two lists of lines compared, no model involved. Answers the
 * question a reviewed workpaper leaves open -- were the findings actually
 * fixed, and what else moved that nobody asked for.
 */
export async function getWorkpaperDiff(
  token: string,
  jobId: string,
  against?: string,
): Promise<WorkpaperDiff> {
  const query = against ? `?against=${encodeURIComponent(against)}` : "";
  return apiRequest<WorkpaperDiff>(
    `/workpapers/${encodeURIComponent(jobId)}/diff${query}`,
    token,
  );
}

// --------------------------------------------------------------------------
// Xero's own Activity Statement, fetched by the Casa browser extension
// --------------------------------------------------------------------------

/** Xero's figures as the extension reports them: box name to amount, plus a few labels. */
export type XeroFigures = Record<string, number | string | boolean | null>;

export interface XeroStatement {
  statement_id: string;
  period_start: string;
  period_end: string;
  figures: XeroFigures;
  file_bytes: number;
  /** Whether Xero's Excel file was saved with the figures. */
  has_file?: boolean;
  fetched_by_name: string;
  fetched_at: number;
}

/** The short code Xero's website uses for this client's organisation. */
export async function getXeroShortcode(
  token: string,
  clientId: string,
): Promise<{ short_code: string; tenant_name: string }> {
  return apiRequest(`/clients/${encodeURIComponent(clientId)}/xero-shortcode`, token);
}

export async function listXeroStatements(
  token: string,
  clientId: string,
  periodEnd?: string,
): Promise<XeroStatement[]> {
  const query = periodEnd ? `?period_end=${encodeURIComponent(periodEnd)}` : "";
  const data = await apiRequest<{ statements: XeroStatement[] }>(
    `/clients/${encodeURIComponent(clientId)}/xero-statements${query}`,
    token,
  );
  return data.statements;
}

export async function saveXeroStatement(
  token: string,
  clientId: string,
  statement: {
    period_start: string;
    period_end: string;
    short_code: string;
    figures: XeroFigures;
    file_base64: string;
    /** A readable name for "fetched by"; the API records who from the token. */
    fetched_by_name?: string;
  },
): Promise<XeroStatement> {
  return apiRequest(`/clients/${encodeURIComponent(clientId)}/xero-statements`, token, {
    method: "POST",
    body: JSON.stringify(statement),
  });
}

/** The browser extension, as a zip, through the signed-in API. */
export async function downloadExtension(token: string): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(`${config.apiBaseUrl}/tools/xero-extension`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new ApiError(response.status, `Download failed (${response.status})`);
  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get("Content-Disposition"), "casa-bas-xero-extension.zip"),
  };
}

export interface XeroCompareBox {
  box: string;
  label: string;
  /** As Xero's statement prints it: whole dollars. */
  xero: string | null;
  /** Xero's lines added up, to the cent. */
  xero_exact: string | null;
  bas: string | null;
  difference: string | null;
  whole_dollar_difference: string | null;
  explained_by_lines: string;
}

export interface XeroCompareLine {
  rate?: string;
  date: string;
  account: string;
  reference?: string;
  details: string;
  gross: string;
  gst: string;
}

export interface XeroComparison {
  job_id: string;
  job_generated: number;
  basis: string;
  line_match_exact: boolean;
  lines_matched: number;
  lines_in_xero: number;
  boxes: XeroCompareBox[];
  only_in_xero: XeroCompareLine[];
  only_in_bas: XeroCompareLine[];
}

/** Xero's saved statement beside BAS's newest finished run for the period. */
export async function compareXeroStatement(
  token: string,
  clientId: string,
  statementId: string,
): Promise<XeroComparison> {
  return apiRequest(
    `/clients/${encodeURIComponent(clientId)}/xero-statements/${encodeURIComponent(statementId)}/compare`,
    token,
  );
}
